'use client';

import {
  useState,
  useCallback,
  useRef,
  forwardRef,
  useImperativeHandle,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  UnifiedImageUpload,
  type UnifiedImageUploadRef,
} from '@/shared/ui';
import {
  getEffectiveType,
  countPdfPages,
  estimateReferenceCost,
  MAX_PDF_PAGES,
} from '@/shared/lib/file-validation';
import { useTranslations } from 'next-intl';
import { useUploadStore } from '@/features/upload';
import {
  usePaywallPayload,
  useTokenBalance,
  useSubscriptionLimits,
  usePlan,
} from '@/features/subscription/model/subscription.selectors';
import { useLimitPaywallStore } from '@/shared/stores/limit-paywall-store';
import { toast } from '@/shared/lib/toast';
import { localizeUploadError } from '@/shared/lib/upload-error';
import { useLimitErrorPaywall } from '@/shared/hooks';
import { useProject } from '@/features/project/hooks/use-projects';
import { projectReferencesService } from '@/shared/api/services/project-references.service';
import { cacheKeys } from '@/shared/lib/cache-invalidation';
import type {
  ProjectReference,
  ProjectReferenceSection,
  ProjectReferenceAspect,
  ProjectReferenceCategory,
} from '@/types/project-reference';
import type { ProcessedFile } from '@/shared/lib/services/image-upload.service';
import { createLogger } from '@/shared/lib/logger';

const logger = createLogger('ProjectReferenceUpload');

interface ProjectReferenceUploadProps {
  projectId: string;
  onSuccess?: (reference: ProjectReference) => void;
  className?: string;
  children?: React.ReactNode;
  onLimitReached?: (categoryType: 'image' | 'document') => void;
  currentImageReferenceCount?: number;
  currentDocumentReferenceCount?: number;
  onUploadStart?: () => void;
  onUploadPreview?: (previewUrl: string | null) => void;
  acceptPdf?: boolean;
  transparent?: boolean;
  // Intake section the upload is tagged with (brief = current state,
  // reference = inspiration). Defaults to `reference`.
  section?: ProjectReferenceSection;
  // Pre-seeded aspect(s) for inline upload from a render reference sub-panel
  // (Color panel → ['color']). Forwarded to the create call; the classifier
  // merges its result on top so the panel's aspect is guaranteed present.
  aspects?: ProjectReferenceAspect[];
  // Output-style role for inline upload from the render Output sub-panel
  // ('output'). Mutually exclusive with `aspects` — output is a render-time
  // role, not an aspect. Forwarded to the create call.
  category?: ProjectReferenceCategory;
}

export interface ProjectReferenceUploadRef {
  openFileDialog: () => void;
  /** Inject pre-selected document/image files into the same upload pipeline
   *  the file dialog uses (used by the Brief unified intake). */
  processFiles: (files: File[]) => void;
}

export const ProjectReferenceUpload = forwardRef<
  ProjectReferenceUploadRef,
  ProjectReferenceUploadProps
>(function ProjectReferenceUpload(
  {
    projectId,
    onSuccess,
    className,
    children,
    onLimitReached,
    currentImageReferenceCount = 0,
    currentDocumentReferenceCount = 0,
    onUploadStart,
    onUploadPreview,
    acceptPdf = true,
    transparent = false,
    section = 'reference',
    aspects,
    category,
  },
  ref
) {
  const t = useTranslations('projectReference');
  const tUpload = useTranslations('fileUpload');
  const [_selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const limits = useSubscriptionLimits();
  const plan = usePlan();
  const tokenBalance = useTokenBalance();
  // Per-building token mode (server `pack.is_unlimited`) — decides whether the
  // document-cost pre-check below applies at all.
  const { data: projectResponse } = useProject(projectId);
  const isTokenModeBuilding =
    projectResponse?.data?.attributes?.pack?.is_unlimited === false;
  const insufficientTokensPayload = usePaywallPayload(
    'PROJECT_REFERENCE_INSUFFICIENT_TOKENS'
  );
  const planAttrs = plan && 'attributes' in plan ? plan.attributes : plan;
  const referencesPricing = planAttrs?.references_pricing;
  const showPaywall = useLimitPaywallStore((s) => s.showPaywall);
  const queryClient = useQueryClient();

  // Subscription cache may be cold on first render — treat unknown limit as
  // "no client-side cap", let the server reject with PROJECT_REFERENCE_LIMIT
  // (typed code) which is already routed to the same paywall surface below.
  // Falling back to a hardcoded number here would block paying users at cold
  // load with a phantom paywall.
  const referenceLimit = limits?.references;
  const hasKnownLimit = typeof referenceLimit === 'number' && referenceLimit > 0;
  const imageRefsRemaining = hasKnownLimit
    ? Math.max(0, referenceLimit - currentImageReferenceCount)
    : Number.POSITIVE_INFINITY;
  const docRefsRemaining = hasKnownLimit
    ? Math.max(0, referenceLimit - currentDocumentReferenceCount)
    : Number.POSITIVE_INFINITY;
  const totalRefsRemaining = hasKnownLimit
    ? imageRefsRemaining + docRefsRemaining
    : Number.POSITIVE_INFINITY;
  const uploadRef = useRef<UnifiedImageUploadRef>(null);

  // Forward openFileDialog to parent
  useImperativeHandle(
    ref,
    () => ({
      openFileDialog: () => {
        uploadRef.current?.openFileDialog();
      },
      processFiles: (files: File[]) => {
        uploadRef.current?.processFiles(files);
      },
    }),
    []
  );

  // Upload store state and actions
  const { createBatch, uploadBatch, uploadSingle } = useUploadStore();

  // Server-driven paywall for PROJECT_REFERENCE_LIMIT_EXCEEDED (422). The
  // shared dialog is shown automatically with server-owned copy; parent's
  // onLimitReached is not needed here (that's the preflight path only).
  const routeLimitErrorToPaywall = useLimitErrorPaywall();

  // Allowed types
  const allowedTypes = acceptPdf
    ? [
        'image/jpeg',
        'image/png',
        'image/webp',
        'image/gif',
        'application/pdf',
        'text/plain',
        'text/markdown',
      ]
    : ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];

  // API callback for creating project references
  const createReferencesApi = useCallback(
    async (processedFiles: ProcessedFile[]) => {
      const uploadData = [];

      for (const file of processedFiles) {
        const contentType = getEffectiveType(file.file);
        logger.info(
          `Document upload started: fileName=${file.file.name}, contentType=${contentType}`
        );
        logger.info(`Document read into memory: size=${file.file.size} bytes`);

        // PDF gate. Only the client can validate before upload — the API
        // has no PDF parser. Reject on parse failure (renamed extension or
        // corrupted file) so we don't burn presign + S3 + classification
        // budget before the LLM chokes on it.
        let pageCount: number | undefined;
        if (contentType === 'application/pdf') {
          const counted = await countPdfPages(file.file);
          if (counted === null) {
            throw new Error(t('upload.pdfUnreadable'));
          }
          if (counted > MAX_PDF_PAGES) {
            throw new Error(
              t('upload.pdfTooManyPages', {
                count: counted,
                max: MAX_PDF_PAGES,
              })
            );
          }
          pageCount = counted;
        }

        const referenceResponse =
          await projectReferencesService.createProjectReference(projectId, {
            md5: file.md5,
            fileName: file.file.name,
            contentType,
            fileSize: file.file.size,
            pageCount,
            // Final (post-downscale) pixel dimensions. ProcessedFile reports 0
            // for non-image references (PDF/txt/md); the service omits them.
            width: file.width,
            height: file.height,
            section,
            aspects,
            category,
          });

        const uploadPolicy = referenceResponse.attributes.upload_policy;

        logger.info(
          `Presign received: referenceId=${referenceResponse.id}, skipUpload=${uploadPolicy.skip_upload === true}`
        );

        uploadData.push({
          id: referenceResponse.id,
          uploadPolicy,
        });
      }

      return uploadData;
    },
    [projectId, section, aspects, category, t]
  );

  const handleUpload = useCallback(
    async (files: File[]) => {
      // The optimistic placeholder card is already on screen: the crop modal
      // emits it synchronously on confirm (onInstantPreview → onUploadPreview),
      // before this background upload even starts. Here we only clear it once
      // the real reference lands in the list (or on failure).
      try {
        const confirm = (id: string) => {
          logger.info(`Confirming upload: referenceId=${id}`);
          return projectReferencesService.confirmUpload(projectId, id);
        };

        let results;

        if (files.length === 1) {
          const file = files[0];
          if (!file) return;

          const result = await uploadSingle(
            file,
            'reference',
            async (processedFile) => {
              const references = await createReferencesApi([processedFile]);
              const uploadData = references[0];
              if (!uploadData) {
                throw new Error('Failed to create reference');
              }
              return uploadData;
            },
            confirm
          );
          results = [result];
        } else {
          const batchId = createBatch(files, 'reference');
          results = await uploadBatch(batchId, createReferencesApi, confirm);
        }

        if (onSuccess) {
          for (const result of results) {
            if (result.confirmed) {
              const ref = result.confirmed as ProjectReference & {
                classification_status?: string;
              };
              logger.info(
                `Document upload completed: referenceId=${ref.id}, classificationStatus=${ref.classification_status ?? 'unknown'}`
              );
              onSuccess(result.confirmed);
            }
          }
        }

        // Invalidate the references query first so the real (pending) row is
        // in the list, then clear the optimistic card — avoids a flash of empty
        // grid between the placeholder disappearing and the real row arriving.
        await queryClient.invalidateQueries({
          queryKey: cacheKeys.references.list(projectId, section),
        });
        onUploadPreview?.(null);

        setSelectedFiles([]);
      } catch (error) {
        // Clear optimistic preview on error
        onUploadPreview?.(null);

        // Server may reject with PROJECT_REFERENCE_LIMIT_EXCEEDED (422). The
        // typed limit is handled by the shared paywall surface — skip the
        // generic toast in that case.
        if (routeLimitErrorToPaywall(error)) {
          return;
        }

        logger.error(
          `Document upload failed: ${error instanceof Error ? error.message : String(error)}`
        );
        // Surface the failure as a toast. Pre-localized client validations
        // (page-count / size limits) and server-translated API errors are shown
        // verbatim; S3-layer failures carry a reason code and get localized.
        toast.error(localizeUploadError(error, tUpload, t('upload.failed')));
      }
    },
    [
      createReferencesApi,
      uploadSingle,
      createBatch,
      uploadBatch,
      onSuccess,
      onUploadPreview,
      projectId,
      queryClient,
      routeLimitErrorToPaywall,
      section,
      t,
      tUpload,
    ]
  );

  // Store files in a ref to avoid race condition with state
  const pendingFilesRef = useRef<File[]>([]);

  // Pre-flight gate: returning false vetoes the flow inside
  // UnifiedImageUpload (no crop queue, no upload) so the paywall never opens
  // underneath an active crop modal.
  const handleFilesSelected = useCallback(
    async (files: File[]): Promise<boolean> => {
      // Split files by type and trim each group to its remaining limit
      const imageFiles = files.filter((f) => f.type.startsWith('image/'));
      const documentFiles = files.filter((f) => !f.type.startsWith('image/'));

      const acceptedImages = imageFiles.slice(0, imageRefsRemaining);
      const acceptedDocuments = documentFiles.slice(0, docRefsRemaining);
      const acceptedFiles = [...acceptedImages, ...acceptedDocuments];

      // Nothing accepted — show upgrade prompt. Pick the category that was
      // actually attempted so the dialog body matches what the user tried
      // (image limit vs document limit — they share the code but copy differs).
      if (acceptedFiles.length === 0) {
        const category: 'image' | 'document' =
          imageFiles.length > 0 ? 'image' : 'document';
        onLimitReached?.(category);
        return false;
      }

      // Some files were trimmed — notify user
      const skippedImages = imageFiles.length - acceptedImages.length;
      const skippedDocuments = documentFiles.length - acceptedDocuments.length;
      if (skippedImages > 0 || skippedDocuments > 0) {
        const parts: string[] = [];
        if (skippedImages > 0) {
          parts.push(t('page.skippedImages', { count: skippedImages }));
        }
        if (skippedDocuments > 0) {
          parts.push(t('page.skippedDocuments', { count: skippedDocuments }));
        }
        toast.warning(parts.join('. '));
      }

      // Token-balance preflight for PDF / txt / md. Images don't hit the LLM
      // on upload path — only documents project a token cost. Skip silently
      // if pricing isn't loaded yet (cold start) and let the server reject
      // later if balance is actually exhausted.
      // Client-side pre-check for document reference cost, per-building: a
      // token-mode building pays from the visible wallet, so the
      // insufficient-tokens paywall fires before the upload starts. An
      // unlimited building (Facade Kit / Pro Plan, server `pack.is_unlimited`)
      // is never token-gated — mirrors the server's buildingRunsUnlimited
      // skip. The server enforces the same balance floor authoritatively.
      if (
        isTokenModeBuilding &&
        referencesPricing &&
        acceptedDocuments.length > 0
      ) {
        const { estimate } = await estimateReferenceCost(
          acceptedDocuments,
          referencesPricing
        );
        if (estimate > tokenBalance) {
          if (insufficientTokensPayload) {
            const substitute = (s: string) =>
              s
                .replace(':estimate', String(estimate))
                .replace(':balance', String(tokenBalance));
            showPaywall(
              {
                ...insufficientTokensPayload,
                title: substitute(insufficientTokensPayload.title),
                body: substitute(insufficientTokensPayload.body),
              },
              'PROJECT_REFERENCE_INSUFFICIENT_TOKENS'
            );
          }
          return false;
        }
      }

      // Store in ref for immediate access in handleUploadComplete
      pendingFilesRef.current = acceptedFiles;
      setSelectedFiles(acceptedFiles);
      onUploadStart?.();
      return true;
    },
    [
      imageRefsRemaining,
      docRefsRemaining,
      onLimitReached,
      onUploadStart,
      referencesPricing,
      tokenBalance,
      isTokenModeBuilding,
      insufficientTokensPayload,
      showPaywall,
      t,
    ]
  );

  const handleUploadComplete = useCallback(
    async (croppedFiles?: File[]) => {
      try {
        // Use cropped files if provided (from crop modal), otherwise use ref
        const filesToUpload = croppedFiles?.length
          ? croppedFiles
          : pendingFilesRef.current;
        if (filesToUpload.length === 0) {
          console.warn('No files to upload');
          return;
        }
        await handleUpload(filesToUpload);
      } catch (_error) {
        // Error is already handled
      }
    },
    [handleUpload]
  );

  // UnifiedImageUpload fires this for failures inside its own pipeline
  // (S3 PUT errors, file-read failures). handleUpload's own catch covers
  // the API-create / page-count / size-validation paths.
  const handleUploadError = useCallback(
    (error: Error) => {
      toast.error(localizeUploadError(error, tUpload, t('upload.failed')));
    },
    [t, tUpload]
  );

  // During upload the optimistic preview card (rendered inline in the grid by
  // ProjectReferencesContent via onUploadPreview) communicates progress — same
  // as Apple's optimistic in-grid card. No full-screen spinner stage here: it
  // blanked the whole grid behind a redundant "Uploading… 100%" bar.

  return (
    <UnifiedImageUpload
      ref={uploadRef}
      uploadType="reference"
      conceptId=""
      allowMultiple={totalRefsRemaining >= 2}
      maxFiles={Math.min(totalRefsRemaining, 10)}
      allowCrop={true}
      cropAspectRatio={1}
      freeAspectRatio={true}
      showRendersOption={true}
      allowedTypes={allowedTypes}
      className={className}
      transparent={transparent}
      onFilesSelected={handleFilesSelected}
      onFilesProcessed={handleUploadComplete}
      onInstantPreview={onUploadPreview}
      onUploadError={handleUploadError}
      uploadDescription={t('upload.dropDescription')}
      accept={
        acceptPdf
          ? 'image/*,application/pdf,text/plain,text/markdown,.md'
          : 'image/*'
      }
    >
      {children}
    </UnifiedImageUpload>
  );
});
